 import { computed } from 'vue';
import {
  useRoute,
  useRouter,
  type RouteLocationNormalizedLoaded,
  type RouteLocationRaw,
} from 'vue-router';
import query from '@/components/query/query-constructor';
import _, { sortBy, uniq } from 'lodash';
import { DrillFilterOptionsEnum, DelimitersEnum } from '@/services/models/Enums';
import { useStore } from '@/store';
import { isSettingsRouteName } from '@/routes/is-settings-route-name';
import {
  buildDedicatedReportTabPath,
  getDedicatedReportGroupBy,
  getDedicatedReportRouteName,
  getReportTypeFromRouteName,
  isDedicatedReportRoute,
} from '@/views/reports/report-route-behavior';
import { useReportParams } from '@/composables/useReportParams';
import { useTableQuery } from '@/composables/useTableQuery';
import { $emitter } from '@/services/emitter';
import type { Route } from 'vue-router';

import type {
  RefreshReportOptions,
  LinkOptions,
  NextReportProps,
} from '@/components/query/url-types';
import { primeReportRouteFlags, reportRouteParams } from '@/components/query/report-route-query';
import { ExcludeTypeEnum } from '@/components/query/url-types';

export type { RefreshReportOptions, LinkOptions, NextReportProps };
export { ExcludeTypeEnum } from '@/components/query/url-types';

const REPORT_DIMENSION_ORDER = [
  'campaigns',
  'offers',
  'flows',
  'landings',
  'traffic-sources',
  'affiliate-networks',
  'countries',
  'cities',
  'states',
  'languages',
  'ips',
  'useragents',
  'subnet-small',
  'subnet-big',
  'isps',
  'mobile-carriers',
  'connection-types',
  'browsers',
  'browser-versions',
  'oses',
  'os-versions',
  'device-models',
  'device-vendors',
  'device-types',
  'screen-resolutions',
  'referrers',
  'referrer-domains',
  'day-of-week',
  'hour-of-day',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
  'day',
  'month',
  'conversions',
  'errors',
  'live-visits',
  'live-clicks',
  'sixth-sense',
];

const routeName = (
  routeLike: RouteLocationNormalizedLoaded | Route,
): string | symbol | null | undefined => {
  const r = routeLike as {
    name?: string | symbol | null;
    value?: { name?: string | symbol | null };
  };
  return r.name ?? r.value?.name;
};

export { isSettingsRouteName } from '@/routes/is-settings-route-name';

export const useUrl = () => {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const reportParamsApi = useReportParams();

  const getDefautLinkOptions = (): LinkOptions => {
    return {
      isReport: true,
      tabId: tableQueryApi.activeReportIndex.value,
    };
  }

  const order = computed(() => {
    const dimensions = store.dimensions as Record<string, unknown>;
    return sortBy(Object.keys(dimensions), (item: string) => {
      return (
        (REPORT_DIMENSION_ORDER.indexOf(item) === -1 && 1000) ||
        REPORT_DIMENSION_ORDER.indexOf(item)
      );
    });
  });

  const nextType = (_type: string) => {};

  const isDashboard = computed(
    () => route.name === 'app.blank' || route.name === 'app.index',
  );

  const isSettingsRoute = computed(() => isSettingsRouteName(route.name));

  const basicType = computed(() => {
    if (isDedicatedReportRoute(route.name)) {
      return getDedicatedReportGroupBy(route.name as string);
    }

    return route.params.type as string;
  });

  const computedType = computed(
    () => tableQueryApi.tableMenuOverride.value || basicType.value || '',
  );

  const isLiveVisits = computed(() =>
    ['live-visits', 'live-clicks'].includes(computedType.value),
  );

  const isClientHintsNoticeVisible = computed(() =>
    [
      'oses',
      'os-versions',
      'browsers',
      'browser-versions',
      'device-models',
      'device-vendors',
      'device-types',
    ].includes(computedType.value),
  );

  const getRouteName = (group: string) => {
    return getDedicatedReportRouteName(group);
  };

  const mainTabs = computed(
    () =>
      [
        'campaigns',
        'offers',
        'landings',
        'flows',
        'traffic-sources',
        'affiliate-networks',
      ] as const,
  );

  const isMainTab = computed(() => mainTabs.value.includes(computedType.value as any));

  const isCustomTab = computed(
    () => !!route.params.type?.toString().startsWith('custom'),
  );

  const getReportType = (currentRoute: RouteLocationNormalizedLoaded | Route) => {
    return getReportTypeFromRouteName(
      routeName(currentRoute),
      (currentRoute as RouteLocationNormalizedLoaded).params.type as string,
    );
  };

  const currentReportType = computed(() => getReportType(route));

  const reportDecoded = computed(() => {
    const report: unknown = route.params.report || tableQueryApi.tableSelectedReport.value;
    return report ? query.decode(report) : report;
  });

  const getDataDecoded = () => {
    const data: any = route.params.data;
    return data ? query.decode(data) : data;
  }

  const dataDecoded = computed(() => getDataDecoded());

  const reportDrillCampaign = computed(() => {
    let result: Bemob.ReportQueryDrill = null as any;
    const rd = reportDecoded.value;
    if (rd) {
      result = rd.drill
        .filter(
          (e: Bemob.ReportQueryDrill) =>
            !e.filterValues.includes(DelimitersEnum.drillFilterOptions),
        )
        .find((e: Bemob.ReportQueryDrill) => e.filterTables === 'campaigns');

      if (
        !result &&
        rd.filterTables === 'campaigns' &&
        !rd.filterValues.includes(DelimitersEnum.drillFilterOptions)
      ) {
        result = {
          filterTables: rd.filterTables,
          filterValues: rd.filterValues,
        };
      }
    }

    return result ? result.filterValues : null;
  });

  const isHidden = (group: any) => {
    if (reportDecoded.value) {
      const hiddenByDrill: string[] = reportDecoded.value.drill
        .filter(
          (item: Bemob.ReportQueryDrill) =>
            item.filterOptions === DrillFilterOptionsEnum.equals,
        )
        .map((item: Bemob.ReportQueryDrill) => item.filterTables);

      let hidden = hiddenByDrill;
      if (reportDecoded.value.filterOptions === DrillFilterOptionsEnum.equals) {
        hidden.push(reportDecoded.value.filterTables);
      }

      return hidden.includes(group);
    }
  }

  const isReport = computed(() => !!route.params.report);

  const isTree = computed(() => {
    const { view } = tableQueryApi.getTableQueryForCurrentTab();
    return isReport.value && view === 'tree';
  });

  const reportQuery = () => {
    return (route.params.report && query.decode(route.params.report as string)) || {};
  }

  const getDefaultNextReportProps = (): NextReportProps => {
    return {
      tableNextReport: tableQueryApi.getTableSelectedItem(),
      tableNextReportGroup: tableQueryApi.tableNextReportGroup.value,
      tableSelectedItems: tableQueryApi.tableSelectedItems.value,
      tableNextReportExclude: false,
      tableNextReportExcludeType: ExcludeTypeEnum.concat,
      tableNextReportOnlyDrill: false,
    };
  }

  const getReportLink = (nextReportProps: NextReportProps = {}) => {
    nextReportProps = Object.assign(getDefaultNextReportProps(), nextReportProps);
    const {
      tableNextReport,
      tableNextReportGroup,
      tableNextReportExclude,
      tableSelectedItems,
    } = nextReportProps;
    const dimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;
    let group = tableNextReportExclude
      ? route.params.type
      : order.value.find((e: any) => route.params.type !== e);
    const {
      columns: storeColumns,
      order: orderCols,
      context,
    } = reportParamsApi.savedColumnsInCurentPreset.value[route.params.type as string];

    const filters = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: context,
      }),
      ...orderCols,
      groupBy: context,
      columns: storeColumns,
    };

    let columns = [...storeColumns];

    columns[0] = (dimensions as Record<string, { mainColumn: string }>)[group as string].mainColumn;

    let next_report_id = tableNextReport.id;

    const filterOptions = tableNextReportExclude
      ? DrillFilterOptionsEnum.not_equals
      : DrillFilterOptionsEnum.equals;

    const filterTables = tableNextReportGroup ? tableNextReportGroup : route.params.type;

    const filterValues = next_report_id
      ? next_report_id
      : tableSelectedItems
          .map((item: any) => item.DT_RowId)
          .join(DelimitersEnum.drillFilterOptions);

    let report_query = {
      group: group,
      filterTables: filterTables,
      filterValues: filterValues,
      filterOptions: filterOptions,
      drill: [] as any[],
    };

    const report = query.encode(report_query);

    const tableQuery = {
      ...filters,
      offset: 0,
      columns,
    };

    const link = `/app/report/${group}/${query.encode(tableQuery)}/${report}`;

    return link;
  }

  const reportLink = computed(() => getReportLink());

  const reportTabLink = computed(() => {
    const { type, data, report } = route.params;

    if (isDedicatedReportRoute(route.name)) {
      return buildDedicatedReportTabPath(route.name as string, data as string, report as string);
    }

    return `/app/report/${type}/${data}/${report}`;
  });

  const decodeReportUrl = (url: string) => {
    const urlRegex = [
      /\/app\/report\/([^\/]+)\/([^\/]+)\/([^\/]+)/,
      /\/app\/live-([^\/]+)\/([^\/]+)\/([^\/]+)/,
      /\/app\/(conversions|errors)\/([^\/]+)\/([^\/]+)/,
    ];

    for (let regex of urlRegex) {
      let match = url.match(regex);
      if (match) {
        return {
          type: match[1],
          data: query.decode(match[2]),
          report: query.decode(match[3]),
        };
      }
    }

    return null;
  }

  const encodeReportUrl = (data: any) => {
    return `/app/report/${data.type}/${query.encode(data.data)}/${query.encode(data.report)}`;
  }

  const normalizeUrl = (url: string) => {
    let decoded = decodeReportUrl(url);
    if (!decoded) {
      return null;
    }

    delete decoded.data.date;
    delete decoded.data.groupBy;
    delete decoded.data.drillPosition;
    delete decoded.data.formatResponse;

    if (decoded.report.levels) {
      decoded.report.levels = decoded.report.levels.filter((item: any) => item);
    } else {
      decoded.report.levels = [];
    }

    return encodeReportUrl(decoded);
  }

  const navigate = (group: string, resetOffset: boolean = false) => {
    if (group === 'undefined') {
      return;
    }

    let data = route.params.data as string;

    if (resetOffset) {
      const dataDecodedLocal = query.decode(data);
      dataDecodedLocal.offset = 0;

      data = query.encode(dataDecodedLocal);
    }

    router.push(`/app/report/${group}/${data}/${route.params.report || ''}`);
  }

  const refreshReport = (options: RefreshReportOptions) => {
    const additionalDataParams = options.additionalDataParams || {};
    const additionalReportParams = options.additionalReportParams || {};
    const manual: any = options.manual || false;
    const manualDrilldown: any = options.manualDrilldown || false;
    const { type } = route.params;

    const data = query.encode({
      ...dataDecoded.value,
      ...additionalDataParams,
    });

    const report = query.encode({
      ...reportDecoded.value,
      ...additionalReportParams,
    });

    if (typeof type === 'undefined') {
      return;
    }

    primeReportRouteFlags({
      manual: !!manual,
      manualDrilldown: !!manualDrilldown,
    });

    router.replace({
      name: 'app.report',
      params: {
        type: type as string,
        data: data,
        report: report,
      },
    });
  }

  const getDashboardLink = (resetReport: boolean = false) => {
    const filters = store.dashboardQuery;

    return `/app/dashboard/${query.encode({
      ...filters,
    })}?resetSelectedReport=${resetReport}`;
  }

  const getHomeLink = () => {
    const routeName = tableQueryApi.tableLastActiveTab.value || 'campaigns';
    return getLink(routeName, {
      isReport: false,
      columnsFromCache: true,
      tabId: 0,
    });
  }

  const defaultHomepage = () => {
    const homepage = store.user.defaultHomepage || 'dashboard';
    let routePath: RouteLocationRaw;

    switch (homepage) {
      case 'campaigns':
        routePath = getHomeLink();
        break;

      case 'dashboard':
      default:
        routePath = getDashboardLink(true);
    }

    return routePath;
  }

  const getLink = (group: string, optionsParam?: LinkOptions): RouteLocationRaw => {
    const options = optionsParam ?? getDefautLinkOptions();
    const { isReport: isReportOpt, tabId } = options;

    const savedColumns = reportParamsApi.savedColumnsInCurentPreset.value;

    let columnsSource: any[] = (savedColumns[group]?.columns || []).map((value: any) => ({
      value,
    }));

    columnsSource = columnsSource || [];

    const columns = columnsSource.map(item => item.value);

    const linkData = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: group,
        tabId: tabId,
      }),
      columns,
    };

    const linkDataEncoded = query.encode(linkData);

    const drill = isReportOpt
      ? route.params.report || tableQueryApi.tableSelectedReport.value
      : null;

    return {
      name: getRouteName(group),
      params: {
        type: group,
        data: linkDataEncoded,
        report: drill,
        ...reportRouteParams({ manual: true }),
      },
    } as RouteLocationRaw;
  }

  const conversionsLink = computed((): any => {
    const {
      order: orderCols,
      columns,
      context,
    } = reportParamsApi.savedColumnsInCurentPreset.value.conversions;
    const filters = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: context,
      }),
      ...orderCols,
      groupBy: context,
      columns,
    };

    return {
      name: 'app.conversions',
      params: {
        data: query.encode(filters),
        report: route.params.report,
        ...reportRouteParams({ manual: true }),
      },
    };
  });

  const getLivesLink = (context: string): string => {
    const { order: orderCols, columns } =
      reportParamsApi.savedColumnsInCurentPreset.value[context];

    const filters = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: context,
      }),
      ...orderCols,
      groupBy: context,
      columns,
    };

    const linkData = query.encode(filters);

    return `/app/${context}/${linkData}/${route.params.report || tableQueryApi.tableSelectedReport.value || ''}`;
  }

  const errorsLink = computed((): any => {
    const {
      order: orderCols,
      columns,
      context,
    } = reportParamsApi.savedColumnsInCurentPreset.value.errors;
    const filters = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: context,
      }),
      ...orderCols,
      columns,
      groupBy: context,
    };

    return {
      name: 'app.errors',
      params: {
        data: query.encode(filters),
        report: route.params.report,
        ...reportRouteParams({ manual: true }),
      },
    };
  });

  const postbacksLink = computed((): any => {
    const {
      order: orderCols,
      columns,
      context,
    } = reportParamsApi.savedColumnsInCurentPreset.value.postbacks;
    const filters = {
      ...reportParamsApi.getReportParamsByTabIdAndGroupBy({
        groupBy: context,
      }),
      ...orderCols,
      columns,
      groupBy: context,
    };

    return {
      name: 'app.postbacks',
      params: {
        data: query.encode(filters),
        report: route.params.report,
        ...reportRouteParams({ manual: true }),
      },
    };
  });

  const getReturnLink = (currentReportId: any) => {
    const group = order.value[0];
    const returnLinkData = query.encode(
      reportParamsApi.getReportParamsByTabIdAndGroupBy({ groupBy: group }),
    );

    return `/app/report/${group}/${returnLinkData}?exitReport=${currentReportId}`;
  }

  const decodeDrilldown = (report: any) => {
    if (!report) return [];

    const encoded = Array.isArray(report) ? report[0] : report;
    const reportQuery = query.decode(String(encoded));
    if (!reportQuery || typeof reportQuery !== 'object') return [];

    const {
      filterTables,
      filterValues,
      filterOptions,
      filterOffset,
      filterLimit,
      drill,
    } = reportQuery;

    return [
      {
        filterTables,
        filterValues: filterValues ?? '',
        filterOptions,
        filterOffset,
        filterLimit,
      },
    ].concat(Array.isArray(drill) ? drill : []);
  }

  const drilldown = computed((): Bemob.ReportQueryDrill[] =>
    decodeDrilldown(route.params.report),
  );

  const drillLink = (
    index: number = undefined as any,
    previousFilter: any = null,
    nextReportProps: NextReportProps = {},
  ) => {
    nextReportProps = Object.assign(getDefaultNextReportProps(), nextReportProps);
    const {
      tableNextReport,
      tableNextReportGroup,
      tableNextReportExclude,
      tableSelectedItems,
      tableNextReportExcludeType,
    } = nextReportProps;

    let middleDrilldown = previousFilter && previousFilter[0];
    let tableTreeLevel = tableQueryApi.getTableQueryForCurrentTab().treeLevels?.[0] as string | undefined;
    let restorePage: Record<string, any> = {};

    let group: string = tableNextReportExclude
      ? (route.params.type as string)
      : middleDrilldown
        ? middleDrilldown.filterTables
        : route.params.type === tableNextReportGroup && tableTreeLevel
          ? tableTreeLevel
          : tableNextReportGroup && route.params.type !== tableNextReportGroup
            ? (route.params.type as string)
            : (order.value.find(
                (e: any) => route.params.type !== e && !isHidden(e),
              ) as string);

    if (group !== route.params.type) {
      $emitter.emit('preserve-levels');
    }

    const dimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;
    const savedColumns = reportParamsApi.savedColumnsInCurentPreset.value;

    let columns = _.cloneDeep(savedColumns[route.params.type as string].columns);

    columns[0] = (dimensions as Record<string, { mainColumn: string }>)[group].mainColumn;

    let filters: any = reportParamsApi.getReportParams();
    let next_report_id = tableNextReport.id;

    let report_query: Bemob.ReportQuery = {
      drill: [],
    };

    let current_report_query = route.params.report;
    if (current_report_query) {
      const current_report: any = query.decode(current_report_query as string);

      report_query.filterTables = current_report.filterTables;
      report_query.filterValues = current_report.filterValues;
      report_query.filterOptions = current_report.filterOptions;

      if (typeof current_report.filterOffset !== 'undefined') {
        report_query.filterOffset = current_report.filterOffset;
      }

      if (typeof current_report.filterLimit !== 'undefined') {
        report_query.filterLimit = current_report.filterLimit;
      }

      report_query.group = current_report.group;

      if (index !== undefined && Number.isInteger(index)) {
        let _drill: Bemob.ReportQueryDrill[] = current_report.drill || [];
        let deleted: Bemob.ReportQueryDrill[];

        if (_drill.length) {
          deleted = _drill.splice(index - 1, 1);
        }

        group =
          deleted && deleted[0] ? deleted[0].filterTables : (route.params.type as string);

        report_query.drill = _drill;

        if (!report_query.drill.length) {
          restorePage = {
            offset: report_query.filterOffset,
            limit: report_query.filterLimit,
          };
        } else {
          const lastDrillIndex = report_query.drill.length - 1;

          restorePage = {
            offset: report_query.drill[lastDrillIndex].filterOffset,
            limit: report_query.drill[lastDrillIndex].filterLimit,
          };
        }
      } else {
        let spliceLevel =
          tableTreeLevel === group ? tableTreeLevel : tableNextReportGroup;

        $emitter.emit('splice-level', spliceLevel);
        report_query.drill = current_report.drill;

        const filterOptions = tableNextReportExclude
          ? DrillFilterOptionsEnum.not_equals
          : DrillFilterOptionsEnum.equals;
        const filterTables = tableNextReportGroup
          ? tableNextReportGroup
          : route.params.type;

        const exists = report_query.drill.find(
          (e: any) => e.filterTables === filterTables,
        );
        const firstDrill = report_query.filterTables === filterTables;

        const getExcludedFilterValues = (itemFilterValues: string) => {
          const newFilterValuesSplited = itemFilterValues.split(
            DelimitersEnum.drillFilterOptions,
          );
          const newTableSelectedItems = tableSelectedItems.map(
            (item: any) => item.DT_RowId,
          );
          const newFilterValues =
            tableNextReportExcludeType === ExcludeTypeEnum.concat
              ? uniq(newFilterValuesSplited.concat(newTableSelectedItems))
              : newFilterValuesSplited.filter(
                  filterValue => !newTableSelectedItems.includes(filterValue),
                );

          return newFilterValues.join(DelimitersEnum.drillFilterOptions);
        };

        if (firstDrill) {
          report_query.filterValues = getExcludedFilterValues(
            report_query.filterValues as string,
          );
          report_query.filterOptions = filterOptions;
        } else if (exists) {
          report_query.drill = report_query.drill.map(item => {
            if (item.filterTables === filterTables) {
              let filterValues = next_report_id
                ? next_report_id
                : getExcludedFilterValues(item.filterValues as string);

              return {
                filterTables: filterTables,
                filterValues: filterValues,
                filterOptions: filterOptions,
              };
            }

            return item;
          });
        } else {
          const filterValues = next_report_id
            ? next_report_id
            : tableSelectedItems
                .map((item: any) => item.DT_RowId)
                .join(DelimitersEnum.drillFilterOptions);

          let newDrill: Bemob.ReportQueryDrill = {
            filterTables: filterTables as string,
            filterValues: filterValues,
            filterOptions: filterOptions,
          };

          const dataDecodedLocal = query.decode(route.params.data as string);

          if (!report_query.drill.length) {
            report_query.filterOffset = dataDecodedLocal.offset;
            report_query.filterLimit = dataDecodedLocal.limit;
          } else {
            const lastDrillIndex = report_query.drill.length - 1;

            report_query.drill[lastDrillIndex].filterOffset = dataDecodedLocal.offset;
            report_query.drill[lastDrillIndex].filterLimit = dataDecodedLocal.limit;
          }

          report_query.drill = report_query.drill.concat([newDrill]);
        }
      }
    }

    filters.offset = 0;

    const report = query.encode(report_query);
    const data = query.encode({
      ...filters,
      columns,
      ...restorePage,
    });

    return {
      name: 'app.report',
      params: {
        type: group,
        data: data,
        report: report,
        ...reportRouteParams({ manualDrilldown: true }),
      },
    };
  }

  const drilldownForward = (
    previousFilter: any = null,
    nextReportProps: NextReportProps = {},
  ) => {
    const link = getDrilldownForwardLink(previousFilter, nextReportProps);
    router.push(link as any);
  };

  const drilldownForwardNewTab = (
    previousFilter: any = null,
    nextReportProps: NextReportProps = {},
  ) => {
    const link = getDrilldownForwardLink(previousFilter, nextReportProps);
    $emitter.emit('tab-add', link);
  };

  const getDrilldownForwardLink = (
    previousFilter: any,
    nextReportProps: NextReportProps,
  ) => {
    const report = reportQuery() as any;
    const rdrill = report.drill || [];

    tableQueryApi.patchTableQueryField({
      name: 'drillPosition',
      value: rdrill.length + 1,
    });

    return drillLink(undefined, previousFilter, nextReportProps);
  }

  const drilldownBack = (index: any) => {
    const report = reportQuery() as any;
    const rdrill = report.drill || [];

    tableQueryApi.patchTableQueryField({
      name: 'drillPosition',
      value: rdrill.length - 1,
    });

    router.push(drillLink(index) as any);
  }

  const getGroupByMetric = (metric: string) => {
    const dimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;

    const column_list = Object.keys(dimensions);
    const columns: { [type: string]: string[] } = {};

    for (let column of column_list) {
      if (metric === dimensions[column].mainColumn) {
        return column;
      }

      columns[column] = dimensions[column].additionalColumns;
    }

    for (let column = column_list.pop(); !!column; column = column_list.pop()) {
      for (let i = 0; i < columns[column].length; i++) {
        if (columns[column][i] === metric) return column;
      }
    }
  }

  const notReports = computed(
    () => ['conversions', 'postbacks', 'errors', 'live-visits', 'live-clicks'] as const,
  );

  const isBasicReport = computed(
    () => !notReports.value.includes(computedType.value as any),
  );

  return {
    route,
    router,
    getDefautLinkOptions,
    order,
    nextType,
    isDashboard,
    isSettingsRoute,
    isLiveVisits,
    isClientHintsNoticeVisible,
    basicType,
    getRouteName,
    computedType,
    mainTabs,
    isMainTab,
    isCustomTab,
    currentReportType,
    getReportType,
    reportDecoded,
    dataDecoded,
    getDataDecoded,
    reportDrillCampaign,
    isHidden,
    isReport,
    isTree,
    reportQuery,
    getReportLink,
    reportLink,
    reportTabLink,
    decodeReportUrl,
    encodeReportUrl,
    normalizeUrl,
    navigate,
    refreshReport,
    getDashboardLink,
    getHomeLink,
    defaultHomepage,
    getLink,
    conversionsLink,
    getLivesLink,
    errorsLink,
    postbacksLink,
    getReturnLink,
    drilldown,
    decodeDrilldown,
    getDefaultNextReportProps,
    drillLink,
    drilldownForward,
    drilldownForwardNewTab,
    getDrilldownForwardLink,
    drilldownBack,
    getGroupByMetric,
    notReports,
    isBasicReport,
  };
};
